import { Button } from '@/components/ui/button'

export interface ReferralAccessBannersProps {
    /** BlockpotOperator.referralManager does not point at the configured ReferralManager. */
    unwired: boolean
    /** The connected wallet lacks the ReferralManager's OPERATOR_ROLE. */
    missingOperatorRole: boolean
    /** The connected wallet holds the operator's DEFAULT_ADMIN_ROLE (can wire in one click). */
    canWire: boolean
    /** The connected wallet holds the RM's DEFAULT_ADMIN_ROLE (can self-grant operator). */
    canGrant: boolean
    busy: boolean
    /** The configured ReferralManager address, shown in the manual wiring instruction. */
    managerAddress: string
    onWire: () => void
    onGrant: () => void
}

// On-chain preflight banners for the referrals page: a confirmed
// role/wiring problem renders as an explanation with one-click remediation when the
// wallet holds the granting role, instead of a bare on-chain revert later.
export function ReferralAccessBanners({
    unwired,
    missingOperatorRole,
    canWire,
    canGrant,
    busy,
    managerAddress,
    onWire,
    onGrant,
}: ReferralAccessBannersProps) {
    return (
        <>
            {unwired && (
                <div
                    data-testid="referral-wiring-warning"
                    className="space-y-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-4 text-sm"
                >
                    <p>
                        The ReferralManager is deployed but not wired into the operator contract.
                        Entries will not route referral fees until{' '}
                        <code className="font-mono text-xs">
                            BlockpotOperator.setReferralManager
                        </code>{' '}
                        is called by the operator admin.
                    </p>
                    {canWire ? (
                        <Button size="sm" disabled={busy} onClick={onWire}>
                            Wire into operator
                        </Button>
                    ) : (
                        <p className="text-muted-foreground">
                            Your wallet does not hold the operator admin role. Ask the operator
                            admin to call{' '}
                            <code className="font-mono text-xs">
                                setReferralManager({managerAddress})
                            </code>
                            .
                        </p>
                    )}
                </div>
            )}

            {missingOperatorRole && (
                <div
                    data-testid="referral-access-warning"
                    className="space-y-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-4 text-sm"
                >
                    <p>
                        Your wallet does not hold the ReferralManager OPERATOR_ROLE — registration,
                        suspension, termination, and clawback transactions would revert on-chain.
                    </p>
                    {canGrant ? (
                        <Button size="sm" disabled={busy} onClick={onGrant}>
                            Grant operator role to my wallet
                        </Button>
                    ) : (
                        <p className="text-muted-foreground">
                            The ReferralManager admin must grant OPERATOR_ROLE to this wallet first.
                        </p>
                    )}
                </div>
            )}
        </>
    )
}
